import { ObjectData } from "@novorender/data-js-api";
import { vec2, vec3, vec4 } from "gl-matrix";
import { useEffect, useState } from "react";

import { EventOrigin, Events, SectionMode, track } from "apis/procore";
import { useAppDispatch, useAppSelector } from "app/redux-store-interactions";
import { useIsolationVisibility } from "components/window/core/objectTree/hooks/useIsolationVisibility";
import { useExplorerGlobals } from "contexts/explorerGlobals";
import { useDispatchHidden, useHidden } from "contexts/hidden";
import { HighlightCollection, useHighlightCollections } from "contexts/highlightCollections";
import { highlightActions, useDispatchHighlighted, useHighlighted } from "contexts/highlighted";
import { useProcoreStampMenuItems } from "contexts/procoreStamp";
import { useDispatchSelectionBasket, useSelectionBasket } from "contexts/selectionBasket";
import { useFlyToSelected } from "features/flyToSelected/useFlyToSelected";
import { useSectionPlaneUndoRedo } from "features/sectionPlane/sectionPlaneUndoRedo";
import { useEnterToolMode } from "features/toolMode/useEnterToolMode";
import { useAbortController } from "hooks/useAbortController";
import { selectIsOnline } from "slices/explorer";
import { telemetry } from "telemetry";
import {
    findSimilarObjects,
    getDefaultSearchCriteria,
    hideObjects,
    hideSimilarObjects,
    isolateObjects,
    isolateObjectsInXRayMode,
    selectSimilarObjects,
    unisolateObjects,
    unisolateObjectsFromXRayMode,
    withLoadingState,
} from "utils/objectActions";
import { getObjectBoundsW, getObjectMetadataRotation } from "utils/objectData";
import { getObjectData } from "utils/search";

import {
    renderActions,
    selectDefaultVisibility,
    selectMainObject,
    selectPreIsolationVisibility,
    selectStamp,
} from "../renderSlice";
import type { Stamp } from "../types";
import { ObjectVisibility, Picker } from "../types";
import { StampKind } from "../types";
import { getLocalRotationAroundNormal } from "../utils";

export type CanvasContextMenuStamp = Extract<Stamp, { kind: StampKind.CanvasContextMenu }>;

export interface UseProcoreStampOptions {
    openProperties: () => void;
}

export interface ProcoreStampData {
    stamp: CanvasContextMenuStamp;
    obj: ObjectData | null;
    isLoading: boolean;
    isOnline: boolean;
    flyToDisabled: boolean;
    multipleObjectsSelected: boolean;
    anyObjectsSelected: boolean;
    highlightedCount: number;
    menuItems: ReturnType<typeof useProcoreStampMenuItems>;
    close: () => void;
    hide: () => void;
    hideSimilar: () => Promise<void>;
    selectSimilar: () => Promise<void>;
    isolate: () => void;
    isolateInXRayMode: () => void;
    isIsolated: boolean;
    isIsolatedInXRay: boolean;
    unisolate: () => void;
    unisolateFromXRayMode: () => void;
    viewProperties: () => void;
    zoomToSelection: () => Promise<void>;
    addSectionPlane: () => Promise<void>;
    executeMenuItem: (item: { id: string; onClick: () => void }) => void;
}

export function useProcoreStamp({ openProperties }: UseProcoreStampOptions): ProcoreStampData | null {
    const dispatch = useAppDispatch();
    const { addClippingPlaneWithUndo } = useSectionPlaneUndoRedo();
    const dispatchHidden = useDispatchHidden();
    const dispatchHighlighted = useDispatchHighlighted();
    const dispatchSelectionBasket = useDispatchSelectionBasket();
    const {
        state: { db, dataAccess, view, canvas },
    } = useExplorerGlobals(true);

    const highlighted = useHighlighted();
    const highlightCollections = useHighlightCollections();
    const hiddenObjects = useHidden();
    const selectionBasket = useSelectionBasket();

    const stamp = useAppSelector(selectStamp);
    const isOnline = useAppSelector(selectIsOnline);
    const mainObject = useAppSelector(selectMainObject);
    const defaultVisibility = useAppSelector(selectDefaultVisibility);
    const preIsolationVisibility = useAppSelector(selectPreIsolationVisibility);
    const isolation = useIsolationVisibility();

    const isIsolated = defaultVisibility === ObjectVisibility.Transparent && preIsolationVisibility !== undefined;
    const isIsolatedInXRay = defaultVisibility === ObjectVisibility.Xray && preIsolationVisibility !== undefined;

    const { flyToSelected, disabled: flyToDisabled } = useFlyToSelected();
    const [obj, setObj] = useState<ObjectData | null>(null);
    const [loading, setLoading] = useState(false);
    const [abortController, abort] = useAbortController();
    const menuItems = useProcoreStampMenuItems();

    const anyObjectsSelected = highlighted.idArr.length >= 1;
    const multipleObjectsSelected = highlighted.idArr.length > 1;

    const enterToolMode = useEnterToolMode();

    const stampObjectId = stamp?.kind === StampKind.CanvasContextMenu ? stamp.data.object : undefined;

    useEffect(() => {
        abort();
    }, [stampObjectId, abort]);

    useEffect(() => {
        if (stamp?.kind !== StampKind.CanvasContextMenu) {
            if (stamp) {
                console.warn("CanvasContextMenuStamp rendered for the wrong stamp kind");
                dispatch(renderActions.setStamp(null));
            }
            return;
        }

        const abortSignal = abortController.current.signal;
        const objectId = stamp.data.object;
        if (!objectId || !stamp.data.position) {
            setObj(null);
            setLoading(false);
            return;
        }

        setLoading(true);
        setObj(null);

        // Always flips `loading` off — even when offline / metadata fetch
        // rejects — so the menu can render its available actions instead of
        // being stuck on ghost placeholders.
        (async () => {
            try {
                const loaded = await getObjectData({ db, id: objectId, view });
                if (!abortSignal.aborted) {
                    setObj(loaded ?? null);
                }
            } catch (e) {
                if (!abortSignal.aborted) {
                    telemetry.trackCaughtException({
                        source: "render.useProcoreStamp.loadObjectData",
                        category: "data",
                        error: e,
                    });
                    setObj(null);
                }
            } finally {
                if (!abortSignal.aborted) {
                    setLoading(false);
                }
            }
        })();
    }, [stamp, db, view, dispatch, abortController]);

    if (!stamp || stamp.kind !== StampKind.CanvasContextMenu) {
        return null;
    }

    const close = () => {
        dispatch(renderActions.setStamp(null));
    };

    const hide = () => {
        if (stamp.data.object === undefined) {
            return;
        }

        const objectsToHide = multipleObjectsSelected ? highlighted.idArr : [stamp.data.object];
        if (isolation.active) {
            const ids =
                mainObject !== undefined && !objectsToHide.includes(mainObject)
                    ? [...objectsToHide, mainObject]
                    : objectsToHide;
            isolation.setIdsVisible(ids, false);
        } else {
            hideObjects(objectsToHide, {
                currentMainObject: mainObject,
                currentHiddenIds: hiddenObjects.ids,
                currentLastHiddenIds: hiddenObjects.lastHiddenIds,
                dispatch,
                dispatchHighlighted,
                dispatchHidden,
                dispatchSelectionBasket,
                preserveSelection: true,
                includeMainObjectToHide: true,
            });
        }
        void track(Events.bimObjects.hidden, { origin: EventOrigin.ContextMenu });
        close();
    };

    const hideSimilar = async () => {
        if (!obj) {
            return;
        }

        close();
        void track(Events.bimObjects.hideSimilar, { origin: EventOrigin.ContextMenu });

        await withLoadingState(
            async () => {
                if (isolation.active) {
                    const criteria = getDefaultSearchCriteria(obj);
                    if (criteria.length === 0) {
                        return;
                    }
                    const objectIds = await findSimilarObjects({ dataAccess, searchCriteria: criteria });
                    isolation.setIdsVisible(objectIds, false);
                    return;
                }
                await hideSimilarObjects(
                    obj,
                    {
                        dataAccess,
                        dispatch,
                        dispatchHighlighted,
                        dispatchHidden,
                        dispatchSelectionBasket,
                        currentHiddenIds: hiddenObjects.idArr,
                        currentMainObject: mainObject,
                        currentHighlightedIds: highlighted.idArr,
                        currentSelectionBasketIds: selectionBasket.idArr,
                        currentLastHiddenIds: hiddenObjects.lastHiddenIds,
                    },
                    undefined,
                );
            },
            { dispatch },
        );
    };

    const selectSimilar = async () => {
        if (!obj) {
            return;
        }

        close();
        void track(Events.bimObjects.selectSimilar, { origin: EventOrigin.ContextMenu });

        await withLoadingState(
            () =>
                selectSimilarObjects(
                    obj,
                    { dataAccess, dispatch, dispatchHighlighted, currentHighlightedIds: highlighted.idArr },
                    undefined,
                ),
            { dispatch },
        );
    };

    const isolate = () => {
        close();
        isolateObjects({
            dispatch,
            isolationIds: highlighted.idArr,
            currentDefaultVisibility: preIsolationVisibility ?? defaultVisibility,
        });
        void track(Events.bimObjects.isolated, { origin: EventOrigin.ContextMenu });
    };

    const isolateInXRayMode = () => {
        close();
        isolateObjectsInXRayMode({
            dispatch,
            currentDefaultVisibility: preIsolationVisibility ?? defaultVisibility,
        });
        void track(Events.bimObjects.isolatedInXray, { origin: EventOrigin.ContextMenu });
    };

    const unisolate = () => {
        close();
        if (preIsolationVisibility === undefined) {
            return;
        }
        unisolateObjects({
            dispatch,
            isolationIds: highlightCollections[HighlightCollection.TransparentSelectionBase].idArr,
            restoreToVisibility: preIsolationVisibility,
        });
        void track(Events.bimObjects.unisolated, { origin: EventOrigin.ContextMenu });
    };

    const unisolateFromXRayMode = () => {
        close();
        if (preIsolationVisibility === undefined) {
            return;
        }
        unisolateObjectsFromXRayMode({ dispatch, restoreToVisibility: preIsolationVisibility });
        void track(Events.bimObjects.unisolatedFromXray, { origin: EventOrigin.ContextMenu });
    };

    const viewProperties = () => {
        close();
        openProperties();
        void track(Events.bimObjects.propertiesShown, { origin: EventOrigin.ContextMenu });
    };

    const zoomToSelection = async () => {
        close();
        void track(Events.bimObjects.zoomedToSelection, { origin: EventOrigin.ContextMenu });
        await withLoadingState(() => flyToSelected(), { dispatch });
    };

    const addSectionPlane = async () => {
        const { position, normal, object } = stamp.data;
        if (!normal || !position) {
            return;
        }

        const w = vec3.dot(normal, position);
        let rotation = 0;
        let wBounds;
        if (object) {
            try {
                const [rotationQuat, bounds] = await Promise.all([
                    getObjectMetadataRotation(view, db, object),
                    getObjectBoundsW(view, db, object, normal),
                ]);
                wBounds = bounds;
                if (rotationQuat) {
                    rotation = getLocalRotationAroundNormal(rotationQuat, normal);
                }
                enterToolMode({ picker: Picker.SectionPlane });
            } catch (ex) {
                console.warn("Error getting object rotation", ex);
                telemetry.trackCaughtException({
                    source: "render.useProcoreStamp.addSectionPlane",
                    category: "render",
                    error: ex,
                });
            }
        }

        const canvasRect = canvas?.getBoundingClientRect();
        addClippingPlaneWithUndo(
            {
                normalOffset: vec4.fromValues(normal[0], normal[1], normal[2], w),
                baseW: w,
                rotation,
                anchorPos: position,
                showPlane: false,
                wBounds,
            },
            canvasRect
                ? {
                      screenPoint: vec2.fromValues(stamp.mouseX - canvasRect.left, stamp.mouseY - canvasRect.top),
                  }
                : undefined,
        );

        dispatchHighlighted(highlightActions.setIds([]));
        dispatch(renderActions.setMainObject(undefined));

        void track(Events.clipPlane.enabled, { mode: SectionMode.ClipPlane, origin: EventOrigin.ContextMenu });
        close();
    };

    const executeMenuItem = (item: { id: string; onClick: () => void }) => {
        close();
        item.onClick();
    };

    return {
        stamp,
        obj,
        isLoading: loading,
        isOnline,
        flyToDisabled,
        multipleObjectsSelected,
        anyObjectsSelected,
        highlightedCount: highlighted.idArr.length,
        menuItems,
        close,
        hide,
        hideSimilar,
        selectSimilar,
        isolate,
        isolateInXRayMode,
        isIsolated,
        isIsolatedInXRay,
        unisolate,
        unisolateFromXRayMode,
        viewProperties,
        zoomToSelection,
        addSectionPlane,
        executeMenuItem,
    };
}
